import { z } from 'zod';
/**
 * Visões do palco: o catálogo fechado de telas que o Secretário (ou um toque
 * no trilho) pode abrir. O modelo escolhe o tipo; o conteúdo vem sempre de
 * dados calculados pelo servidor ou pelo app, nunca escritos pelo modelo.
 */
export const VIEW_KINDS = [
    /** Painel de chegada: o que espera a pessoa e o que o dia tem. */
    'arrival',
    'agenda_day',
    'bills',
    'deliveries',
    'notebook',
    'roadmap',
    /** Aviso curto, que some sozinho. */
    'notice',
];
export const viewKindSchema = z.enum(VIEW_KINDS);
/** Cartão (uma informação), painel (lista curta) ou página (documento, lista longa). */
export const viewLevelSchema = z.enum(['card', 'panel', 'page']);
/** De onde veio o pedido para abrir: toque no trilho, na alça, ou o Secretário. */
export const viewSourceSchema = z.enum(['rail', 'handle', 'assistant', 'history']);
export const stageViewSchema = z.object({
    id: z.string().min(1).max(60),
    kind: viewKindSchema,
    level: viewLevelSchema,
    source: viewSourceSchema.default('rail'),
    /** Texto do aviso, quando a visão é um cartão de aviso. */
    text: z.string().max(140).optional(),
});
/**
 * O que o resultado de uma ferramenta pede à tela: abrir um painel do catálogo
 * ou mostrar um aviso curto. Montado por código no servidor, nunca pelo modelo.
 */
export const viewHintSchema = z.object({
    kind: viewKindSchema,
    level: z.enum(['card', 'panel']),
    /** Texto do aviso (só nos cartões de aviso). */
    text: z.string().max(140).optional(),
    /** Nome do item de que se fala: a linha dele pulsa uma vez no painel. */
    focus: z.string().max(80).optional(),
});
/** Ferramentas da conversa ao vivo que o aparelho trata: abrir e fechar painéis. */
export const SCREEN_SHOW_TOOL = 'mostrar_na_tela';
export const SCREEN_CLOSE_TOOL = 'fechar_tela';
/** O que fechar: só o cartão de cima, o painel, a página aberta por cima, ou tudo (o padrão). */
export const SCREEN_CLOSE_TARGETS = ['cartao', 'painel', 'pagina', 'tudo'];
/** Nome falado de cada painel e a visão que ele abre. */
export const SCREEN_TARGETS = {
    hoje: 'arrival',
    agenda: 'agenda_day',
    contas: 'bills',
    documentos: 'deliveries',
};
/**
 * Pergunta de esclarecimento na tela (só nos apps novos): quando falta um dado
 * para agir, a pergunta com 2 a 4 opções curtas para tocar.
 */
export const SCREEN_ASK_TOOL = 'perguntar_na_tela';
export const ASK_MAX_OPTIONS = 4;
/** "Mostra de novo": reabre o último painel que saiu da tela (só nos apps novos). */
export const SCREEN_AGAIN = 'de_novo';
/** Painéis a mais que os apps novos sabem abrir a pedido (os antigos não conhecem). */
export const SCREEN_PANELS_EXTRA = {
    caderno: 'notebook',
    cartela: 'roadmap',
};
/**
 * Páginas inteiras que o Secretário pode abrir a pedido: lista branca fechada.
 * Nada de plano, ajustes, aparelhos nem exclusão de conta (`VIEW_BLOCKED_ROUTES`).
 */
export const SCREEN_PAGES = {
    semana: '/agenda?ver=semana',
    lista: '/habilidade/lista_compras',
    noticias: '/habilidade/noticias',
    clima: '/habilidade/clima',
    horoscopo: '/habilidade/horoscopo',
    datas: '/habilidade/datas_importantes',
    radar: '/habilidade/radar_precos',
    equipe: '/equipe',
    conversas: '/conversas',
    contatos: '/contatos',
    entregas: '/entregas',
    servicos: '/servicos',
    /** Só é oferecida na voz quando o app declara `goals` (apps antigos não têm a página). */
    objetivos: '/objetivos',
    /** Só quando o app declara `recipes`. */
    receitas: '/receitas',
    /** Só quando o app declara `idiomas`. */
    idiomas: '/idiomas',
    /** Só quando o app declara `clinic` (Meu negócio › Clínica). */
    clinica: '/negocio/clinica',
    clinica_agenda: '/negocio/clinica/agenda',
    clinica_pacientes: '/negocio/clinica/pacientes',
    clinica_numeros: '/negocio/clinica/numeros',
    clinica_catalogo: '/negocio/clinica/catalogo',
};
/** Rotas que o Secretário nunca abre sozinho (dinheiro, conta e segurança). */
export const VIEW_BLOCKED_ROUTES = ['/plano', '/ajustes', '/aparelhos', '/excluir-conta'];
